import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { company, fmtDate } from '../data/common';
import './legal.css';

const brand = company.brand;
const entity = company.name;

export const policyLinks = [
  ['/terms', 'Booking Terms'],
  ['/cancellation-policy', 'Cancellation & Refund Policy'],
  ['/privacy-policy', 'Privacy Policy'],
];

const contactLines = [
  `${entity}, ${company.address}`,
  `Email: ${company.supportEmail} · Phone: ${company.phone} (24x7)`,
];

/** A policy page: [id, heading, paragraphs or bullet lists] sections with a contents list. */
function LegalPage({ path, title, intro, sections }) {
  const { hash } = useLocation();
  // Links such as /privacy-policy#cookies open at that section.
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView?.();
  }, [hash]);
  return (
    <div className="container section legal">
      <header className="legal-head">
        <h1>{title}</h1>
        <p className="muted small">Last updated {fmtDate(company.policiesUpdated)} · Applies to {brand}, operated by {entity}</p>
        <nav className="legal-tabs" aria-label="Policies">
          {policyLinks.map(([to, label]) => (
            <Link key={to} to={to} className={`chip ${to === path ? 'active' : ''}`} aria-current={to === path ? 'page' : undefined}>{label}</Link>
          ))}
        </nav>
      </header>
      <div className="legal-layout">
        <nav className="legal-toc" aria-label="Contents">
          <b>Contents</b>
          <ol>{sections.map(([id, heading]) => <li key={id}><a href={`#${id}`}>{heading}</a></li>)}</ol>
        </nav>
        <article className="legal-body">
          {intro.map((p) => <p key={p}>{p}</p>)}
          {sections.map(([id, heading, blocks]) => (
            <section key={id} id={id}>
              <h2>{heading}</h2>
              {blocks.map((block, i) => (Array.isArray(block)
                ? <ul key={i}>{block.map((item) => <li key={item}>{item}</li>)}</ul>
                : <p key={i}>{block}</p>))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}

export function BookingTerms() {
  return (
    <LegalPage
      path="/terms"
      title="Booking Terms"
      intro={[
        `These terms apply when you use the ${brand} website or app ("${brand}", "we", "us") to search for or book travel. ${brand} is a brand of ${entity}, a company incorporated in India. By making a booking you confirm that you have read and accepted these terms, our Cancellation & Refund Policy and our Privacy Policy.`,
      ]}
      sections={[
        ['role', `Our role`, [
          `${brand} is a booking intermediary. Flights, hotels, buses, cabs, activities, holidays and insurance are provided by independent airlines, hotels, bus and cab operators, activity providers, tour operators and insurers ("suppliers"). Your contract for the travel service itself is with the supplier, and their own conditions of carriage, hotel policies, fare rules and policy wordings also apply.`,
          'We are responsible for arranging your booking correctly and for passing on your payment, details and requests to the supplier. We are not responsible for how the supplier performs the service, including delays, schedule changes, overbooking, quality of rooms or vehicles, or acts of their staff.',
        ]],
        ['account', 'Your account', [
          'You must be at least 18 years old to make a booking. Bookings for minors must be made by a parent or guardian.',
          `If you book without signing in, we add the booking to the ${brand} account for your email address. If none exists, we create one and email you a temporary password. Keep your password confidential; you are responsible for activity on your account.`,
        ]],
        ['details', 'Traveller details and documents', [
          'Enter every name exactly as it appears on the passport or government photo ID that will be carried. Name changes are often not allowed, or are charged by the supplier.',
          [
            'Each traveller must carry a valid government photo ID, and for international travel a passport valid for at least 6 months after the trip, plus any visas, transit visas and health documents required.',
            'Some bookings need a PAN, passport number or ID proof (for example, many hotel rates in India and some bus operators). These are sent to the supplier and are required by them or by Indian tax rules.',
            'Ladies seats on buses can only be booked for female passengers, and the primary bus passenger must be 18 or older.',
            'You are responsible for the accuracy of all details you give. Losses caused by incorrect details are not refundable.',
          ],
        ]],
        ['prices', 'Prices and payment', [
          [
            'Prices are shown in Indian Rupees (INR) and include taxes unless stated otherwise. If you choose another display currency, those amounts are approximate; you are charged in INR and your bank may apply its own conversion.',
            'A convenience fee may apply. It is shown separately before you pay and is not refundable.',
            'Supplier prices change often. We re-check the price before booking; if it has gone up, we ask you to confirm the new price before charging you.',
            'Promo codes are subject to their own conditions (validity, minimum booking value, products, number of uses) and cannot be exchanged for cash.',
            'Payments are processed securely by our payment partners. A booking is confirmed only when you receive a booking confirmation with a supplier reference (such as a PNR or hotel confirmation number).',
          ],
        ]],
        ['products', 'Product-specific terms', [
          [
            "Flights: the airline's fare rules apply to changes, cancellations, baggage and seats. Check-in deadlines, boarding and denied boarding are governed by the airline and DGCA regulations.",
            'Hotels: check-in and check-out times, ID requirements, security deposits and local taxes collected at the property are set by the hotel. Early check-in, late check-out and special requests are subject to availability.',
            "Bus tickets: reach the boarding point at least 15 minutes before the boarding time. The operator may change the bus, seats or timings; the operator is responsible for the journey and for luggage carried.",
            'Transfers: driver and vehicle details are shared before pickup. Waiting beyond the included free time, extra stops and route changes are charged by the operator.',
            "Travel insurance: the policy is issued by the insurer, which alone decides claims under the policy wording. Read the policy document emailed to you.",
            'Holidays and activities: inclusions, itineraries and timings are as described for each package or activity and may change for operational reasons, weather or local regulations.',
          ],
        ]],
        ['changes', 'Changes and cancellations', [
          'Cancellations and refunds follow our Cancellation & Refund Policy and the supplier’s rules shown at booking. Changes, where the supplier allows them, may cost a fee plus any fare or rate difference.',
          'If a supplier cancels or materially changes your booking, we will tell you as soon as we are told and help you with a refund or an alternative.',
        ]],
        ['liability', 'Our liability', [
          `To the extent permitted by law, ${brand}'s total liability for any booking is limited to the amount you paid us for that booking. We are not liable for indirect or consequential losses, or for losses caused by the supplier, by you, or by events beyond our reasonable control such as natural disasters, strikes, epidemics, government action or system failures of airlines and other suppliers.`,
        ]],
        ['use', 'Acceptable use', [
          'Do not make speculative, false or fraudulent bookings, resell bookings without our consent, or use automated tools to access the site. We may cancel such bookings and suspend the accounts involved.',
        ]],
        ['law', 'Governing law and disputes', [
          `These terms are governed by the laws of India. Please contact us first so we can try to resolve any complaint. Disputes are subject to the jurisdiction of the courts at the place of ${entity}'s registered office.`,
          `We may update these terms; the version in force when you book applies to that booking.`,
        ]],
        ['contact', 'Contact and grievances', [
          ...contactLines,
          `Grievance Officer: ${company.grievanceOfficer.name}, ${company.grievanceOfficer.email}. We acknowledge complaints within 48 hours and aim to resolve them within one month.`,
        ]],
      ]}
    />
  );
}

export function CancellationPolicy() {
  return (
    <LegalPage
      path="/cancellation-policy"
      title="Cancellation & Refund Policy"
      intro={[
        `This policy explains how cancellations and refunds work for bookings made on ${brand}. Each booking also shows the supplier's own cancellation rules before you pay and in My Trips; where those rules are more specific, they apply.`,
      ]}
      sections={[
        ['how', 'How to cancel', [
          [
            'Sign in and open the booking from My Trips. Where the supplier allows online cancellation, the refund is shown before you confirm.',
            `If online cancellation isn't available, call ${brand} support on ${company.phone} (24x7) with your booking ID.`,
            'Cancellation times are measured against the departure, pickup or check-in time in the local time shown on your booking.',
          ],
        ]],
        ['general', 'What is refunded', [
          [
            'Refund = amount paid − supplier cancellation charges − any service fee below. The convenience fee is not refunded.',
            'If a promo discount was applied, the refund is calculated on the amount you actually paid.',
            'No-shows (not travelling or not checking in without cancelling) are usually not refundable.',
            'If the supplier cancels the service (for example, a cancelled flight or bus trip), you get a full refund of the amount paid for that service, or an alternative where the supplier offers one.',
          ],
        ]],
        ['flights', 'Flights', [
          [
            "Airline cancellation charges follow the fare rules of your ticket and are per passenger, per sector.",
            `A ${brand} service fee of ₹299 per booking applies to cancellations.`,
            'Cancel at least 3 hours before departure for domestic flights and 4 hours for international flights (or earlier if the airline requires), otherwise the booking may be treated as a no-show.',
            'Statutory taxes and fees are refunded as per airline rules even for non-refundable fares.',
          ],
        ]],
        ['hotels', 'Hotels', [
          [
            'Each rate has its own cancellation windows, shown on the hotel page, at checkout and on your voucher (times in India time). Free cancellation rates are fully refundable until the date shown.',
            'Non-refundable rates cannot be refunded once booked.',
            'Late cancellations and no-shows are charged as per the rate, up to the full booking amount.',
          ],
        ]],
        ['bus', 'Bus tickets', [
          [
            "The operator's cancellation policy applies, with a refund percentage that depends on how many hours before departure you cancel. It is shown on the seat selection page and on your e-ticket.",
            'Some operators allow cancelling individual seats; otherwise all seats on the ticket are cancelled together.',
            'No refund if you miss the bus. If the operator cancels the trip, the full amount is refunded.',
          ],
        ]],
        ['transfers', 'Transfers', [
          [
            'Each cab shows its cancellation rules; many are free to cancel up to 24 hours before pickup.',
            'Cancellations within the non-refundable window and no-shows are not refunded.',
          ],
        ]],
        ['insurance', 'Travel insurance', [
          [
            'A policy can be cancelled for a refund before the cover start date, subject to the insurer’s terms.',
            'Once cover has started, or if a claim has been made, the policy cannot be refunded.',
          ],
        ]],
        ['holidays', 'Holidays and activities', [
          [
            'Cancellation charges for holiday packages and activities are given in the package or activity details and in your booking confirmation, and increase closer to the travel date.',
            'Flights, hotels and other services included in a package also follow their suppliers’ rules.',
          ],
        ]],
        ['timeline', 'Refund timelines', [
          [
            'Refunds go back to the original payment method.',
            'We process refunds within 5–7 working days of the supplier confirming the cancellation. Your bank or card issuer may take a few more days to credit it.',
            'Some airline and hotel refunds are confirmed by the supplier offline; we keep you updated and pay the refund as soon as it is confirmed.',
          ],
        ]],
        ['contact', 'Questions', contactLines],
      ]}
    />
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage
      path="/privacy-policy"
      title="Privacy Policy"
      intro={[
        `${entity} ("we", "us"), which operates ${brand}, respects your privacy. This policy explains what personal data we collect, why, who we share it with and the choices you have. It is issued under the Digital Personal Data Protection Act, 2023, the Information Technology Act, 2000 and the rules made under them.`,
      ]}
      sections={[
        ['collect', 'Data we collect', [
          [
            'Account details: name, email address, mobile number and password (stored only in encrypted, hashed form).',
            'Traveller details needed for a booking: names, title, gender, age or date of birth, nationality, and where the supplier or law requires it, passport details, PAN or another ID proof.',
            'Booking details: what you booked, dates, seats, special requests, prices paid, promo codes and booking references.',
            'Payment details: processed by our payment partners. We do not store full card numbers or UPI PINs.',
            'Technical data: IP address, device and browser type, pages visited and error logs, used to run and secure the service.',
            'Messages you send us, such as holiday enquiries and support requests.',
          ],
        ]],
        ['use', 'How we use it', [
          [
            'To make, manage and change your bookings, send tickets, vouchers and updates, and handle cancellations and refunds.',
            'To create and secure your account, including signing you in and resetting your password.',
            'To provide customer support and respond to complaints.',
            'To prevent fraud and misuse, and to comply with legal, tax and regulatory requirements.',
            'To improve our services. With your consent, to send offers; you can opt out at any time.',
          ],
          'We process your data on the basis of your consent, given when you book or create an account, and for legitimate uses permitted by law, such as completing the booking you asked for and meeting legal obligations.',
        ]],
        ['share', 'Who we share it with', [
          [
            'Suppliers who provide your travel service (airlines, hotels, bus and cab operators, insurers, activity and tour providers) and the travel technology companies that connect us to them. They receive only what they need for your booking.',
            'Payment partners and banks, to process payments and refunds.',
            'Service providers who help us run the platform, such as hosting and email providers, under confidentiality obligations.',
            'Government and law enforcement authorities, immigration and airport authorities, where required by law.',
          ],
          'For international travel, your details are necessarily shared with suppliers and authorities outside India. We do not sell your personal data.',
        ]],
        ['cookies', 'Cookies and browser storage', [
          'We use essential browser storage to keep you signed in during your visit, remember your display currency, keep your recent selections and keep a copy of your bookings on your device so you can see them in My Trips. Only the last digits of a PAN or ID number are kept on your device.',
          'You can clear this storage from your browser settings at any time; you will then need to sign in again and bookings shown only on that device will be removed from it.',
        ]],
        ['retention', 'How long we keep it', [
          'We keep booking and account data for as long as your account is active and as long as needed to provide the service, resolve disputes and meet legal, tax and accounting requirements. After that, it is deleted or anonymised.',
        ]],
        ['security', 'Security', [
          'We use reasonable security safeguards, including encrypted connections (HTTPS), hashed passwords and restricted access to personal data. No system is completely secure; please keep your password confidential and tell us at once if you suspect misuse of your account.',
        ]],
        ['rights', 'Your rights', [
          [
            'Access a summary of your personal data and how it is processed.',
            'Correct, complete or update your data.',
            'Erase your data, unless we must keep it to complete a booking or by law.',
            'Withdraw consent. This does not affect processing already done, and we may be unable to make new bookings without the data needed.',
            'Nominate another person to exercise your rights in case of death or incapacity.',
            'Raise a grievance with us, and then with the Data Protection Board of India.',
          ],
          `To use these rights, write to ${company.supportEmail} from your registered email address.`,
        ]],
        ['children', 'Children', [
          'Our services are for adults. Personal data of children travelling on a booking is provided by, and processed with the consent of, their parent or guardian.',
        ]],
        ['changes', 'Changes to this policy', [
          'We may update this policy. We will post the new version here with its date and, for significant changes, tell you by email or on the site.',
        ]],
        ['grievance', 'Grievance Officer', [
          `${company.grievanceOfficer.name}, ${entity}, ${company.address}. Email: ${company.grievanceOfficer.email}.`,
          'We acknowledge complaints within 48 hours and aim to resolve them within one month.',
        ]],
      ]}
    />
  );
}
